import { MapStyle } from 'appConstants';
import { ColorScheme } from 'appTypes';

import { getMetricColorSchemes } from '../chart/colorMaps';

export const TL_METRIC_ABS_SPEED = 'speed_abs';
export const TL_METRIC_FF_REL_SPEED = 'speed_ff';
export const TL_METRIC_ABS_VOL = 'count_abs';
export const TL_METRIC_TYP_REL_SPEED = 'speed_typ';
export const TL_METRIC_TYP_REL_VOL = 'count_typ';
export const TL_METRIC_TYP_REL_SPEED_VOL = 'count_speed_typ';
export const TL_METRIC_FF_REL_SPEED_VOL = 'count_speed_ff';
export const TL_METRIC_FF_VHD = 'vhd';
export const TL_METRIC_TYP_VHD = 'typ_vhd';
export const TL_METRIC_TYP_RT = 'speed_typ_rt';
export const TL_METRIC_FF_RT = 'speed_ff_rt';
export const TL_METRIC_TYP_VNRT = 'speed_typ_vnrt';
export const TL_METRIC_FF_VNRT = 'speed_ff_vnrt';

const METRIC_PLACEHOLDER = 'metric';

export const TL_METRICS_OPTIONS_NRT = [
  TL_METRIC_TYP_REL_SPEED_VOL,
  TL_METRIC_FF_REL_SPEED_VOL,
  TL_METRIC_TYP_REL_VOL,
  TL_METRIC_TYP_VHD,
];

export const TL_METRICS_OPTIONS_VNRT = [
  TL_METRIC_TYP_RT,
  TL_METRIC_FF_RT,
  TL_METRIC_TYP_VNRT,
  TL_METRIC_FF_VNRT,
];

export const TL_METRICS_OPTIONS_VNRT_EQUIVALENT = {
  [TL_METRIC_FF_RT]: TL_METRIC_FF_VNRT,
  [TL_METRIC_TYP_RT]: TL_METRIC_TYP_VNRT,
};

export const TL_METRICS_OPTIONS_RT_EQUIVALENT = {
  [TL_METRIC_FF_VNRT]: TL_METRIC_FF_RT,
  [TL_METRIC_TYP_VNRT]: TL_METRIC_TYP_RT,
};

export function isRealtimeMetric(metric) {
  return TL_METRICS_OPTIONS_VNRT_EQUIVALENT[metric] !== undefined;
}

export function isVNRTMetric(metric) {
  return TL_METRICS_OPTIONS_RT_EQUIVALENT[metric] !== undefined;
}

export const SPEEDS_NULL_PLACEHOLDER_THRESHOLD = 1000000;
const SPEEDS_NULL_PLACEHOLDER_VALUE = SPEEDS_NULL_PLACEHOLDER_THRESHOLD * 100;
const speeds = (timeOfDayOffset) => [
  'coalesce',
  ['get', `s${timeOfDayOffset}`],
  SPEEDS_NULL_PLACEHOLDER_VALUE,
];

const typicalSpeeds = (timeOfDayOffset) => [
  'coalesce',
  ['get', `ts${timeOfDayOffset}`],
  1 / SPEEDS_NULL_PLACEHOLDER_VALUE,
];
const rtSpeed = (timeOfDayOffset) => [
  'coalesce',
  ['feature-state', 'rtspeed'],
  -1,
];
const vnrtSpeed = (timeOfDayOffset) => [
  'coalesce',
  [
    'coalesce',
    ['feature-state', `s${timeOfDayOffset}`],
    speeds(timeOfDayOffset),
  ],
  -1,
];
const vnrtFFSpeeds = (timeOfDayOffset) => ['get', 'vnrt_freeflow_kph'];
const fallbackdTypicalSpeeds = (timeOfDayOffset) => [
  'coalesce',
  ['get', `ts${timeOfDayOffset}`],
  vnrtFFSpeeds(timeOfDayOffset),
];
const counts = (timeOfDayOffset) => [
  'coalesce',
  ['get', `c${timeOfDayOffset}`],
  0,
];
const typicalCounts = (timeOfDayOffset) => [
  'coalesce',
  ['get', `tc${timeOfDayOffset}`],
  0,
];

export function metricExprDeepSub(expr: Array<any>, sub) {
  // Substitutes METRIC_PLACEHOLDER for the passed sub
  for (let i = 0; i < expr.length; i++) {
    if (expr[i] === METRIC_PLACEHOLDER) {
      expr[i] = sub;
    } else if (Array.isArray(expr[i])) {
      expr[i] = metricExprDeepSub(expr[i], sub);
    }
  }
  return expr;
}

const ff_relative_speed_expr = ({ timeOfDayOffset }) => [
  '/',
  speeds(timeOfDayOffset),
  [
    'coalesce',
    ['get', 'freeflow_speed_kph'],
    1 / SPEEDS_NULL_PLACEHOLDER_VALUE,
  ],
];
const typ_relative_speed_expr = ({ timeOfDayOffset }) => [
  '/',
  speeds(timeOfDayOffset),
  typicalSpeeds(timeOfDayOffset),
];

const typ_relative_count_expr = ({ timeOfDayOffset }) => [
  'interpolate',
  ['linear'],
  [
    '*',
    [
      '-',
      ['/', counts(timeOfDayOffset), ['+', typicalCounts(timeOfDayOffset), 1]],
      1,
    ],
    [
      'interpolate',
      ['linear'],
      ['max', typicalCounts(timeOfDayOffset), counts(timeOfDayOffset)],
      0,
      -0.1,
      50,
      -1,
    ],
  ],
  -5,
  -2,
  -1,
  -1,
  1,
  1,
];

export const counts_absolute_size_expr = (
  timeOfDayOffset,
  hourlyMaxCount,
  base,
) => [
  'interpolate',
  ['linear'],
  [
    '/',
    [
      '*',
      ['max', counts(timeOfDayOffset), typicalCounts(timeOfDayOffset)],
      100,
    ],
    1000,
  ],
  0,
  1.0 * base,
  10,
  1.5 * base,
  100,
  3.0 * base,
];

const legend_stops_0_1_perc = [
  {
    value: 0,
    label: ['Congested', '0'],
  },
  {
    value: 0.2,
    label: ['', '20'],
  },
  {
    value: 0.4,
    label: ['', '40'],
  },
  {
    value: 0.6,
    label: ['', '60'],
  },
  {
    value: 0.8,
    label: ['', '80'],
  },
  {
    value: 1,
    label: ['Typical', '100'],
  },
];

const legend_stops_0_1_ff = [
  {
    value: 0,
    label: ['Congested', '0'],
  },
  {
    value: 0.2,
    label: ['', '20'],
  },
  {
    value: 0.4,
    label: ['', '40'],
  },
  {
    value: 0.6,
    label: ['', '60'],
  },
  {
    value: 0.8,
    label: ['', '80'],
  },
  {
    value: 1,
    label: ['Freeflow', '100'],
  },
];

const legend_stops_1_m1_perc_inverted = [
  {
    value: -2,
    label: ['Higher than normal', '500'],
  },
  {
    value: -1,
    label: ['', '100'],
  },
  {
    value: -0.5,
    label: ['', '50'],
  },
  {
    value: -0.25,
    label: ['', ''],
  },
  {
    value: 0,
    label: ['', 'Typical'],
  },
  {
    value: 0.25,
    label: ['', ''],
  },
  {
    value: 1,
    label: ['Lower than normal', '-100'],
  },
];

const legend_stops_100_0_congestion = [
  {
    value: 0,
    label: ['Congested', '0'],
  },
  {
    value: 20,
    label: ['', '20'],
  },
  {
    value: 40,
    label: ['', '40'],
  },
  {
    value: 60,
    label: ['', '60'],
  },
  {
    value: 80,
    label: ['', '80'],
  },
  {
    value: 100,
    label: ['Freeflow', '100'],
  },
];

export type LegendColorSchemes = {
  continuous?: Record<ColorScheme, any>;
  discrete: Record<ColorScheme, any>;
};

export type LegendStops = Array<{
  label: string | string[];
  value: number;
}>;

export interface IMetricLegendConfig {
  metric_expr?: (params: any) => any[];
  legend_stops: LegendStops;
  legend_title?: string;
  color_schemes?: LegendColorSchemes;
  width?: string;
  shown?: any[];
  colors?: any;
  display_volume_thickness?: boolean;
  is_metric_diverging: boolean;
}

export function getTLMetricOptions(
  mapStyle: MapStyle,
  metric: string,
): IMetricLegendConfig {
  switch (metric) {
    case TL_METRIC_FF_REL_SPEED_VOL:
      return {
        metric_expr: ff_relative_speed_expr,
        legend_stops: legend_stops_0_1_ff,
        color_schemes: getMetricColorSchemes('0_1_stops', mapStyle),
        width: 'count_absolute',
        shown: ['<', METRIC_PLACEHOLDER, 0.99],
        is_metric_diverging: false,
      };
    case TL_METRIC_TYP_REL_SPEED_VOL:
      return {
        metric_expr: typ_relative_speed_expr,
        color_schemes: getMetricColorSchemes('0_1_stops', mapStyle),
        legend_stops: legend_stops_0_1_perc,
        width: 'count_absolute',
        shown: ['<', METRIC_PLACEHOLDER, 0.99],
        is_metric_diverging: false,
      };
    case TL_METRIC_TYP_RT:
      return {
        metric_expr: ({ timeOfDayOffset }) => [
          'interpolate',
          ['linear'],
          [
            '/',
            rtSpeed(timeOfDayOffset),
            fallbackdTypicalSpeeds(timeOfDayOffset),
          ],
          -1,
          1,
          0.001,
          1,
          0.002,
          0,
          1,
          1,
        ], // Compensate for missing speeds data,
        color_schemes: getMetricColorSchemes('0_1_stops', mapStyle),
        legend_stops: legend_stops_0_1_perc,
        legend_title: '% relative speed',
        is_metric_diverging: false,
      };
    case TL_METRIC_FF_RT:
      return {
        metric_expr: ({ timeOfDayOffset }) => [
          'interpolate',
          ['linear'],
          ['/', rtSpeed(timeOfDayOffset), vnrtFFSpeeds(timeOfDayOffset)],
          -1,
          1,
          0.001,
          1,
          0.002,
          0,
          1,
          1,
        ], // Compensate for missing speeds data,
        color_schemes: getMetricColorSchemes('0_1_stops', mapStyle),
        legend_stops: legend_stops_0_1_ff,
        legend_title: '% relative speed',
        is_metric_diverging: false,
      };
    case TL_METRIC_TYP_VNRT:
      return {
        metric_expr: ({ timeOfDayOffset }) => [
          'interpolate',
          ['linear'],
          [
            '/',
            vnrtSpeed(timeOfDayOffset),
            fallbackdTypicalSpeeds(timeOfDayOffset),
          ],
          -1,
          1,
          -0.001,
          1,
          0,
          0,
          1,
          1,
        ], // Compensate for missing speeds data,
        color_schemes: getMetricColorSchemes('0_1_stops', mapStyle),
        legend_stops: legend_stops_0_1_perc,
        legend_title: '% relative speed',
        display_volume_thickness: false,
        is_metric_diverging: false,
      };
    case TL_METRIC_FF_VNRT:
      return {
        metric_expr: ({ timeOfDayOffset }) => [
          'interpolate',
          ['linear'],
          ['/', vnrtSpeed(timeOfDayOffset), vnrtFFSpeeds(timeOfDayOffset)],
          -1,
          1,
          -0.001,
          1,
          0,
          0,
          1,
          1,
        ], // Compensate for missing speeds data,
        color_schemes: getMetricColorSchemes('0_1_stops', mapStyle),
        legend_stops: legend_stops_0_1_ff,
        legend_title: '% relative speed',
        display_volume_thickness: false,
        is_metric_diverging: false,
      };
    case TL_METRIC_TYP_VHD:
      return {
        // atypical delay
        metric_expr: ({ timeOfDayOffset, hourlyMaxCount }) => [
          '-',
          100,
          [
            '*',
            [
              '*',
              [
                'max',
                ['-', 1, typ_relative_speed_expr({ timeOfDayOffset })],
                0,
              ], // 0 - 1 congestion
              ['/', ['*', counts(timeOfDayOffset), 100], hourlyMaxCount], // 0 - 100% of max traffic
            ],
            7, // Its never all of the cars at standstill so cut off the range at 15% VHD
          ],
        ],
        color_schemes: getMetricColorSchemes('100_0_stops', mapStyle),

        legend_stops: legend_stops_100_0_congestion,
        legend_title: 'Trip weighted congestion',
        shown: ['<', METRIC_PLACEHOLDER, 99],
        display_volume_thickness: false,
        is_metric_diverging: false,
      };
    case TL_METRIC_TYP_REL_VOL:
      return {
        metric_expr: typ_relative_count_expr,
        color_schemes: getMetricColorSchemes('volume_stops', mapStyle),
        legend_stops: legend_stops_1_m1_perc_inverted,
        width: 'count_absolute',
        shown: ['>', ['abs', METRIC_PLACEHOLDER], 0.01],
        is_metric_diverging: true,
      };
  }
}
